import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitcher from "@/components/LangSwitcher";
import { Wine } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(form.email, form.password);
      toast.success(t("login_welcome_toast"));
      if (next) nav(next);
      else if (u.role === "admin") nav("/admin");
      else if (u.role === "journalist") nav("/press");
      else nav("/portal");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50 via-white to-wine-50/40">
      <div className="absolute right-6 top-6 z-10"><LangSwitcher /></div>
      <div className="mx-auto grid min-h-screen max-w-7xl items-center px-6 py-10 lg:grid-cols-2 lg:gap-16">
        <div className="hidden lg:block">
          <Logo />
          <div className="mt-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-wine-100 px-3 py-1 text-xs font-semibold text-wine-700"><Wine className="h-3.5 w-3.5" /> {t("innovate_together")}</span>
            <h1 className="mt-6 text-5xl font-bold leading-tight text-navy">
              {t("login_brand_title_pre")} <span className="text-wine-600">{t("login_brand_title_brand")}</span>
            </h1>
            <p className="mt-5 max-w-md text-lg text-neutral-600">{t("login_brand_subtitle")}</p>
          </div>
        </div>

        <div>
          <div className="mb-6 lg:hidden"><Logo /></div>
          <div className="rounded-3xl border border-wine-100 bg-white p-8 shadow-sm md:p-10" data-testid="login-card">
            <h2 className="text-3xl font-bold text-navy">{t("login_title")}</h2>
            <p className="mt-1 text-sm text-neutral-600">{t("login_welcome_back")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("email")}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="login-email" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("password")}</label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-wine-600 hover:text-wine-700 hover:underline" data-testid="login-forgot">Mot de passe oublié ?</Link>
                </div>
                <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-wine-300" data-testid="login-password" />
              </div>
              {error && <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="login-error">{error}</div>}
              <button disabled={loading} type="submit" className="w-full rounded-full bg-wine-500 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 disabled:opacity-50" data-testid="login-submit">
                {loading ? t("login_loading") : t("login")}
              </button>
            </form>
            <div className="mt-5 text-center text-sm text-neutral-600">
              {t("login_no_account")} <Link to="/register" className="font-semibold text-wine-600 hover:text-wine-700" data-testid="login-to-register">{t("login_create_account")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
